/**
 * Which build is this tab running, and is a newer one live?
 *
 * Defera is a single-page app. A tab opened before a deploy keeps executing
 * the JavaScript it loaded, because every navigation after the first is
 * client-side and never re-fetches index.html. So a fix can be live on the
 * server and invisible to someone who simply left the tab open.
 *
 * That bit us twice on 2026-09-21 alone: Muse's concierge login looked like a
 * customer account, and the local-time change never reached open chats. Both
 * were stale tabs, not broken deploys. A third report that day, the Change
 * Flight cabin fix still showing "Economy", was blamed on a stale tab and was
 * a real bug (see src/utils/cabinClass.js). Prove a tab is stale before
 * blaming it.
 *
 * The check is deliberately cheap and deliberately polite:
 *   - The running commit is baked into the bundle at build time
 *     (`__BUILD_COMMIT__`, from VERCEL_GIT_COMMIT_SHA — the same value
 *     /api/health reports, sliced the same way).
 *   - The deployed commit comes from /api/health, which already exists for
 *     exactly this ("makes a deploy externally verifiable").
 *   - A mismatch shows a banner. It NEVER reloads on its own: an automatic
 *     reload mid-checkout or mid-chat would destroy the very thing the
 *     customer was doing, which is worse than running yesterday's code.
 *   - The one exception is a flight search (src/lib/searchNavigation.js):
 *     pressing Search leaves the form anyway and the whole search travels
 *     in the URL, so the press loads the new build and nothing is lost.
 */

/** The commit this bundle was built from. `local` in dev, or in any context
 *  (tests, SSR) where the build-time constant was never injected. */
/* global __BUILD_COMMIT__ */
export const BUILD_COMMIT =
  typeof __BUILD_COMMIT__ !== 'undefined' && __BUILD_COMMIT__ ? String(__BUILD_COMMIT__) : 'local';

/** Values that mean "we don't know which build this is". */
const UNKNOWN = new Set(['', 'local', 'unknown', 'dev', 'undefined', 'null']);

const clean = (v) => String(v ?? '').trim().toLowerCase().slice(0, 8);

/**
 * Is `deployed` a DIFFERENT, KNOWN build from `running`?
 *
 * Both sides must be known. An unknown side — local dev, a health check that
 * failed, a proxy that stripped the field — must read as "no update", never
 * as "update": a banner that fires on every page in development, or whenever
 * the network blinks, trains everyone to ignore it.
 *
 * Compared on the 8-character prefix because that is what /api/health
 * publishes; a full SHA on one side would otherwise never match.
 */
export function isNewerBuildAvailable(running, deployed) {
  const r = clean(running);
  const d = clean(deployed);
  if (UNKNOWN.has(r) || UNKNOWN.has(d)) return false;
  return r !== d;
}

/**
 * Should a check run now? Throttles the three triggers — first load, the tab
 * coming back into view, and the periodic timer — so a user flicking between
 * tabs cannot turn this into a request storm.
 */
export function shouldCheckNow({ lastCheckedMs = 0, nowMs = Date.now(), minIntervalMs = 60_000 } = {}) {
  return !lastCheckedMs || nowMs - lastCheckedMs >= minIntervalMs;
}

/**
 * The commit production is serving right now, or null. Never throws: this
 * runs quietly in the background of every page, and a failure must degrade
 * to "no banner", not to an error.
 */
export async function fetchDeployedCommit({ fetchImpl = typeof fetch !== 'undefined' ? fetch : null } = {}) {
  if (!fetchImpl) return null;
  try {
    // no-store: the whole question is "what is live NOW", so a cached answer
    // from before the deploy would defeat the point.
    const res = await fetchImpl('/api/health', { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.commit ? String(body.commit) : null;
  } catch {
    return null;
  }
}

// What production served at the last check, shared by everyone who asks:
// the banner (on load, on return to the tab, every ten minutes) and the
// search forms, which ask again as soon as a search is being filled in so a
// deploy that went live seconds ago is known by the time Search is pressed.
const deployed = { commit: null, checkedAt: 0, inFlight: null };

/**
 * Ask /api/health — unless an answer younger than `maxAgeMs` is on hand or
 * a check is already on its way. Resolves with the deployed commit, or null
 * when unknown; never throws. Skipped entirely when this build doesn't know
 * its own commit (local dev, tests): no answer could make it "older".
 */
export function refreshDeployedCommit({ maxAgeMs = 60_000, now = Date.now, fetchImpl, running = BUILD_COMMIT } = {}) {
  if (UNKNOWN.has(clean(running))) return Promise.resolve(null);
  if (deployed.inFlight) return deployed.inFlight;
  if (!shouldCheckNow({ lastCheckedMs: deployed.checkedAt, nowMs: now(), minIntervalMs: maxAgeMs })) return Promise.resolve(deployed.commit);
  deployed.checkedAt = now();
  deployed.inFlight = fetchDeployedCommit(fetchImpl ? { fetchImpl } : undefined).then((commit) => {
    if (commit) deployed.commit = commit;
    deployed.inFlight = null;
    return deployed.commit;
  });
  return deployed.inFlight;
}

/** The commit production served at the last check, or null. */
export const lastDeployedCommit = () => deployed.commit;

/** Is a newer build than this one live, as of the last check? */
export function newerBuildIsLive({ running = BUILD_COMMIT } = {}) {
  return isNewerBuildAvailable(running, deployed.commit);
}

/** Tests only: forget every check. */
export function resetDeployedCommitForTests() {
  deployed.commit = null;
  deployed.checkedAt = 0;
  deployed.inFlight = null;
}
